/**
 * #1197 — express a Polymarket slug's matchup in its game key's code space.
 *
 * A Polymarket game slug names its two sides in Poly's own spelling and in
 * game-day order (`cfb-nw-mst` = Northwestern at Michigan State). Hydra's game
 * key names the same two sides in canonical codes but ALPHA-SORTED
 * (`ncaaf|2026-10-17|1|MSU|NW`), and `canonicalTeamNames` is keyed by those
 * canonical codes. A row that displays the slug codes therefore misses its
 * names (`MST` is not a key of {MSU, NW}) and builds a Game Detail route the
 * key does not match.
 *
 * The binder decides which canonical code each slug side is, using only
 * evidence about THIS game, strongest first:
 *
 *   1. hydra's own statement that a slug side is a canonical team — the Poly
 *      moneyline's `canonicalYesTeam`, the YES outcome's team, which on today's
 *      wire is the slug-first side (observed, not a contract promise). Core
 *      binds it from the YES outcome's school NAME, so it holds where codes
 *      lie: Poly `sdst` is San Diego State while key `SDST` is South Dakota
 *      State (Dan, #1197, 2026-10-29: on all 233 live keyed CFB moneylines);
 *   2. without that statement, a slug code that IS one of the key's two codes
 *      binds to itself;
 *   3. and when exactly one side binds that way, the other slug side is the
 *      other key code — the key holds this game's two teams and the slug names
 *      the same two. Rules 2–3 trust code equality, which is why the stamp
 *      outranks them.
 *
 * It never orients from the key's order: the key is sorted, not seated, and
 * reading it as away/home flipped Wisconsin @ UCLA and New Mexico @ Hawaii on
 * the moneyline board (the 2026-10-14 report). Nor does it read titles, names
 * or a slug alias table. When the evidence is missing or malformed, the answer
 * is null and the caller keeps the slug codes: an honest alias beats a
 * plausible wrong school.
 */

/** A matchup in game-day order. */
export interface OrientedCodes {
  awayCode: string;
  homeCode: string;
}

/** Hydra's claim that the slug side spelled `slugCode` is canonical `team`. */
export interface SlugSideIdentity {
  slugCode: string;
  team: string;
}

const norm = (code: string | null | undefined): string => (code ?? '').trim().toUpperCase();

/**
 * The slug matchup re-expressed in the key's codes, still in slug (game-day)
 * order, or null when this game's evidence does not settle both sides.
 * `keyCodes` is the key's pair in any order; `identity`, when given, must name
 * one of the two slug sides and one of the two key codes, and then decides the
 * answer on its own.
 */
export function bindPolySlugPair(
  slug: OrientedCodes,
  keyCodes: readonly [string, string],
  identity?: SlugSideIdentity | null,
): OrientedCodes | null {
  const away = norm(slug.awayCode);
  const home = norm(slug.homeCode);
  const lo = norm(keyCodes[0]);
  const hi = norm(keyCodes[1]);
  if (!away || !home || !lo || !hi || away === home || lo === hi) return null;
  const inKey = (code: string) => code === lo || code === hi;
  const otherKey = (code: string) => (code === lo ? hi : lo);

  if (identity) {
    const side = norm(identity.slugCode);
    const team = norm(identity.team);
    // A claim about a side this slug does not have, or a team outside this
    // game, is not evidence about this matchup — and since hydra stamped it on
    // this game's row, it is a contradiction rather than an absence.
    if (!inKey(team) || (side !== away && side !== home)) return null;
    return side === away
      ? { awayCode: team, homeCode: otherKey(team) }
      : { awayCode: otherKey(team), homeCode: team };
  }
  if (inKey(away) && inKey(home)) return { awayCode: away, homeCode: home };
  if (inKey(away)) return { awayCode: away, homeCode: otherKey(away) };
  if (inKey(home)) return { awayCode: otherKey(home), homeCode: home };
  return null;
}
